import { FC, Suspense } from 'react';
import { useSelector } from 'react-redux';
import cn from 'classnames';
import { BLOCKS } from '@contentful/rich-text-types';

import type { Store } from 'src/redux/types/storeTypes';
import type { Block, ImageWrapper } from 'src/__generated__/graphqlTypes';
import { setItemInObjectBySlug } from 'lib/sharedMethods.service';
import { capitalizeFirstLetter } from 'lib/text.service';
import classes from 'src/styles/_commonClasses.module.scss';
import { CommonRichText as Title, DataLink } from 'components/Shared/SharedComponents';
import type { RichTextParsersConfig } from 'types/objectTypes';

import PredefinedReview from './PredefinedReview';
import styles from './PredefinedReviews.module.scss';
import MobileCarousel from './MobileCarousel';

import { IconSlug as Slug, slugs } from '../config/constants';
import { IPredefinedReviews } from '../config/declarations';

const parsersConfig: RichTextParsersConfig = {
  [BLOCKS.HEADING_2]: {
    classNames: cn(styles.title, styles.titleH2),
  },
  [BLOCKS.HEADING_3]: {
    classNames: cn(styles.title, styles.titleH3),
  },
};

const PredefinedReviews: FC<IPredefinedReviews> = ({
  blocks,
  bgColor,
  extraData,
}) => {
  const isMobileViewWidth = useSelector((store: Store) => store.server.app.isMobileViewWidth);
  const { verticalPadding, view = 'filled' } = extraData || {};
  const [block] = blocks;

  const {
    contentTypesCollection,
    imagesCollection,
    link,
    richTitle,
  } = block;

  if (!block) {
    return null;
  }

  const customerReviews = contentTypesCollection?.items as Array<Block> || [];
  const reducer = setItemInObjectBySlug(slugs);
  const {
    chat,
    star,
  } = (imagesCollection?.items as Array<ImageWrapper>)
    ?.reduce(reducer, {} as Record<Slug, ImageWrapper>) || {};

  const bindReviews = () => customerReviews.map((block) => (
    <PredefinedReview
      key={block.entryName}
      block={block}
      chatImage={chat.image}
      starImage={star.image}
    />
  ));

  return (
    <section
      className={cn(
        styles.wrapper,
        verticalPadding && styles.reduceTopSpacing,
        classes[`sharedWrapperPaddingVertical${capitalizeFirstLetter(verticalPadding)}`],
        {
          [styles.common]: view === 'common',
          [styles.filled]: view !== 'common',
        },
      )}
      style={{ background: bgColor! }}
    >
      {view === 'filled' && (
        <div className={styles.heading}>
          <Title content={richTitle} parsersConfig={parsersConfig} />
          {link && (
            <DataLink link={link} className={styles.link}>
              {link.title}
              {' >'}
            </DataLink>
          )}
        </div>
      )}
      <div className={styles.review}>
        {isMobileViewWidth
          ? (
            <Suspense fallback={bindReviews()}>
              <MobileCarousel>
                {bindReviews()}
              </MobileCarousel>
            </Suspense>
          )
          : bindReviews()}
      </div>
    </section>
  );
};

export default PredefinedReviews;
